import { useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { PriceTable } from "./PriceTable";
import { gruposDoCatalogo, mapaDePrecos, precoAjustado, rowKey, soOsDiferenciados, type CatalogItem } from "./precos-comum";

/** Os preços de um cliente (clínica parceira com configuração individual): o percentual geral e os
 *  preços fixos por serviço. O mesmo editor do Catálogo › Preços e da aba Preços do cadastro. */
export function ClientPriceEditor({ clientOrganizationId, locked }: { clientOrganizationId: string; locked: boolean }) {
  const utils = trpc.useUtils();
  const [percentInput, setPercentInput] = useState("");
  const [soDiferenciados, setSoDiferenciados] = useState(false);
  const catalogQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const priceListQuery = trpc.labPricing.priceList.list.useQuery();
  const profileQuery = trpc.labPricing.clientPricing.getProfile.useQuery({ clientOrganizationId });
  const overridesQuery = trpc.labPricing.clientPricing.listOverrides.useQuery({ clientOrganizationId });

  const invalidar = () => {
    utils.labPricing.priceList.list.invalidate();
    utils.labPricing.clientPricing.getProfile.invalidate({ clientOrganizationId });
    utils.labPricing.clientPricing.listOverrides.invalidate({ clientOrganizationId });
  };
  const aoSalvar = (msg: string) => ({ onSuccess: () => (invalidar(), toast.success(msg)), onError: (e: { message: string }) => toast.error(e.message) });
  const upsertProfile = trpc.labPricing.clientPricing.upsertProfile.useMutation(aoSalvar("Percentual atualizado."));
  const setOverride = trpc.labPricing.clientPricing.setOverride.useMutation(aoSalvar("Preço personalizado salvo."));
  const removeOverride = trpc.labPricing.clientPricing.removeOverride.useMutation(aoSalvar("Preço restaurado pro padrão."));

  const percent = profileQuery.data?.adjustmentPercent ?? 0;
  const base = useMemo(() => mapaDePrecos(priceListQuery.data), [priceListQuery.data]);
  const overrides = useMemo(() => mapaDePrecos(overridesQuery.data), [overridesQuery.data]);
  const grupos = useMemo(() => {
    const ativos = ((catalogQuery.data ?? []) as CatalogItem[]).filter((c) => c.active);
    return gruposDoCatalogo(ativos, (s, m) => precoAjustado(base.get(rowKey(s, m)) ?? null, overrides.get(rowKey(s, m)), percent));
  }, [catalogQuery.data, base, overrides, percent]);
  const visiveis = soDiferenciados ? soOsDiferenciados(grupos) : grupos;

  const aplicarPercentual = () => {
    const valor = Number(percentInput.replace(",", "."));
    if (!Number.isFinite(valor)) return toast.error("Percentual inválido.");
    upsertProfile.mutate({ clientOrganizationId, adjustmentPercent: valor });
  };
  const restaurar = (serviceCategoryId: string, materialOptionId: string | null) => {
    const linha = (overridesQuery.data ?? []).find((o) => rowKey(o.serviceCategoryId, o.materialOptionId) === rowKey(serviceCategoryId, materialOptionId));
    if (linha) removeOverride.mutate({ id: linha.id });
  };

  return (
    <div>
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-border dark:bg-background/40">
        <span className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">Aplicar desconto/acréscimo geral:</span>
        <input
          value={percentInput}
          onChange={(e) => setPercentInput(e.target.value)}
          placeholder={String(percent)}
          aria-label="Percentual do cliente"
          className="h-8 w-20 rounded-md border border-zinc-200 bg-white px-2 text-right text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
        />
        <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">%</span>
        {!locked && (
          <button
            type="button"
            onClick={aplicarPercentual}
            disabled={upsertProfile.isPending || !percentInput.trim()}
            className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            Aplicar
          </button>
        )}
        <span className="text-[11px] text-zinc-400 dark:text-muted-foreground">Percentual atual: {percent}%</span>
      </div>
      <label className="mt-3 flex w-fit items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-muted-foreground">
        <input
          type="checkbox"
          checked={soDiferenciados}
          onChange={(e) => setSoDiferenciados(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
        />
        Ver apenas itens com preço fixo diferenciado nesta clínica
      </label>
      <div className="mt-4">
        {catalogQuery.isLoading || priceListQuery.isLoading ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Carregando...</p>
        ) : soDiferenciados && visiveis.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum item com preço fixo diferenciado nesta clínica ainda.</p>
        ) : (
          <PriceTable
            groups={visiveis}
            onSave={(s, m, priceCents) => setOverride.mutate({ clientOrganizationId, serviceCategoryId: s, materialOptionId: m ?? undefined, priceCents })}
            onRestore={locked ? undefined : restaurar}
            saving={locked || setOverride.isPending || removeOverride.isPending}
          />
        )}
      </div>
    </div>
  );
}
